import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronRight, ChevronLeft, Loader2, ShieldCheck } from 'lucide-react';
import { BACKEND_URL } from '../../config/api';

const EMPLOYER_TYPES = [
  { value: 'builder_main_contractor', label: 'Builder / main contractor' },
  { value: 'trade_contractor_subcontractor', label: 'Trade contractor / subcontractor' },
  { value: 'site_construction_manager', label: 'Site / construction manager' },
  { value: 'property_developer_landlord', label: 'Property developer / landlord' },
  { value: 'homeowner', label: 'Homeowner' },
  { value: 'other_direct_hirer', label: 'Other direct hirer' },
];

// Employers matching these types must supply a business/trading name.
const BUSINESS_TYPES = new Set([
  'builder_main_contractor',
  'trade_contractor_subcontractor',
  'site_construction_manager',
  'property_developer_landlord',
]);

const STEPS = [
  { key: 'basics', label: 'Basics' },
  { key: 'business', label: 'Business type' },
  { key: 'declaration', label: 'Declaration' },
];

export default function EmployerOnboarding() {
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState(null);
  const [form, setForm] = useState({ direct_hire_declaration: false });

  const hydrate = useCallback(async () => {
    try {
      const me = (await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true })).data || {};
      setForm({
        name: me.name || '',
        phone: me.phone || '',
        employer_type: me.employer_type || '',
        business_name: me.business_name || '',
        postcode: me.postcode || '',
        company_number: me.company_number || '',
        is_limited_company: me.is_limited_company || false,
        direct_hire_declaration: !!me.direct_hire_declaration,
      });
      const s = (await axios.get(`${BACKEND_URL}/api/onboarding/state`, { withCredentials: true })).data;
      setState(s);
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => { hydrate(); }, [hydrate]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const saveStep = async (payload, step) => {
    setSaving(true);
    try {
      const res = await axios.patch(`${BACKEND_URL}/api/onboarding/employer`,
        { step, ...payload }, { withCredentials: true });
      setState(res.data);
      return res.data;
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail.message : detail || e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const active = STEPS[stepIdx];

  const goNext = async () => {
    let payload = {};
    if (active.key === 'basics') {
      if (!form.name || !form.phone || !form.postcode) {
        toast.error('Please answer every field');
        return;
      }
      payload = { name: form.name, phone: form.phone, postcode: form.postcode };
    } else if (active.key === 'business') {
      if (!form.employer_type) { toast.error('Please pick an employer type'); return; }
      const needsBusiness = BUSINESS_TYPES.has(form.employer_type);
      if (needsBusiness && !form.business_name) { toast.error('Please add your business / trading name'); return; }
      payload = { employer_type: form.employer_type };
      if (form.business_name) payload.business_name = form.business_name;
      if (form.is_limited_company) {
        if (!form.company_number) { toast.error('Please add your Companies House number'); return; }
        payload.is_limited_company = true;
        payload.company_number = form.company_number;
      } else {
        payload.is_limited_company = false;
      }
    }
    const res = await saveStep(payload, active.key);
    if (res) setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  };

  const submitForApproval = async () => {
    if (!form.direct_hire_declaration) {
      toast.error('Please tick the direct-hiring declaration to continue.');
      return;
    }
    // Save declaration then submit.
    const saved = await saveStep({ direct_hire_declaration: true }, 'declaration');
    if (!saved) return;
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/onboarding/employer/submit`,
        {}, { withCredentials: true });
      setState(res.data);
      toast.success('Submitted for approval — you can browse trades while we review.');
      navigate('/onboarding/employer/pending', { replace: true });
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail.message : detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="employer-onboarding">
      <div className="max-w-2xl mx-auto p-4 sm:p-6 md:p-8">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Get set up to hire</h1>
          <p className="text-sm text-slate-500 mt-1">Fill these in once and we&apos;ll approve your account so you can contact trades.</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 sm:p-6">
          <div className="flex flex-wrap gap-1.5 mb-4">
            {STEPS.map((s, i) => (
              <span key={s.key} data-testid={`emp-step-badge-${s.key}`}
                className={`text-[11px] px-2 py-1 rounded-full font-medium ${i === stepIdx ? 'bg-orange-100 text-orange-700' : i < stepIdx ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                {s.label}
              </span>
            ))}
          </div>

          {active.key === 'basics' && (
            <div className="space-y-3" data-testid="emp-step-basics">
              <div><label className="text-sm font-medium">Full name</label>
                <input className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200" value={form.name || ''}
                  onChange={(e) => update({ name: e.target.value })} data-testid="emp-input-name" /></div>
              <div><label className="text-sm font-medium">Mobile number</label>
                <input className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200" value={form.phone || ''}
                  onChange={(e) => update({ phone: e.target.value })} data-testid="emp-input-phone" /></div>
              <div><label className="text-sm font-medium">Base postcode</label>
                <input className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200" value={form.postcode || ''}
                  onChange={(e) => update({ postcode: e.target.value.toUpperCase() })} data-testid="emp-input-postcode" /></div>
            </div>
          )}

          {active.key === 'business' && (
            <div className="space-y-3" data-testid="emp-step-business">
              <label className="text-sm font-medium">Which best describes you?</label>
              <div className="grid grid-cols-1 gap-2">
                {EMPLOYER_TYPES.map((t) => (
                  <button key={t.value} type="button" onClick={() => update({ employer_type: t.value })}
                    className={`text-left px-3 py-2 rounded-lg border text-sm ${form.employer_type === t.value ? 'border-orange-500 bg-orange-50' : 'border-slate-200'}`}
                    data-testid={`emp-type-${t.value}`}>
                    {t.label}
                  </button>
                ))}
              </div>
              {BUSINESS_TYPES.has(form.employer_type) && (
                <div><label className="text-sm font-medium">Business / trading name</label>
                  <input className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200" value={form.business_name || ''}
                    onChange={(e) => update({ business_name: e.target.value })} data-testid="emp-input-business" /></div>
              )}
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" checked={!!form.is_limited_company}
                  onChange={(e) => update({ is_limited_company: e.target.checked })} data-testid="emp-limited-check" />
                <span>I hire through a limited company</span>
              </label>
              {form.is_limited_company && (
                <div><label className="text-sm font-medium">Companies House number</label>
                  <input className="w-full mt-1 h-10 px-3 rounded-lg border border-slate-200" value={form.company_number || ''}
                    onChange={(e) => update({ company_number: e.target.value })} data-testid="emp-input-company-number" /></div>
              )}
            </div>
          )}

          {active.key === 'declaration' && (
            <div className="space-y-3" data-testid="emp-step-declaration">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-900 leading-relaxed">
                    <p>Please tick to confirm:</p>
                    <p className="mt-2 font-medium">
                      &ldquo;I am hiring tradespeople for my own business, project or property.
                      I am not a recruitment agency, labour agency, lead seller or third-party
                      labour supplier.&rdquo;
                    </p>
                  </div>
                </div>
              </div>
              <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="emp-declaration-label">
                <input type="checkbox" checked={!!form.direct_hire_declaration}
                  onChange={(e) => update({ direct_hire_declaration: e.target.checked })}
                  data-testid="emp-declaration-check" />
                <span>I agree — I am a direct hirer, not an agency or lead seller.</span>
              </label>
              <p className="text-xs text-slate-500">
                After submission, an admin will review your account (usually within 1 business day).
                While you wait, you can browse trades in the public search —               you just cannot contact them yet.
              </p>
            </div>
          )}

          <div className="flex items-center justify-between mt-6 gap-3">
            <button type="button" disabled={stepIdx === 0 || saving}
              onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
              className="btn-secondary flex items-center gap-1 disabled:opacity-50" data-testid="emp-back">
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
            {active.key === 'declaration' ? (
              <button type="button" onClick={submitForApproval} disabled={saving || !form.direct_hire_declaration}
                className="btn-primary flex items-center gap-1 disabled:opacity-50" data-testid="emp-submit-approval">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Submit for approval
              </button>
            ) : (
              <button type="button" onClick={goNext} disabled={saving}
                className="btn-primary flex items-center gap-1 disabled:opacity-50" data-testid="emp-next">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Next <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
